import moment from 'moment';
import { useState } from 'react';
import ModuleCardMenusNew from './ModuleCardMenusNew';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../reducers/modalReducer';
import { getRequest, postRequest, showMessage } from '../../config';
import ModuleCardHeader from './moduleFace/ModuleCardHeader';
import ModuleCardDescription from './moduleFace/ModuleCardDescription';
import ModuleCardStats from './moduleFace/ModuleCardStats';
import CertificationBadge from './moduleFace/CertificationBadge';
import PassFailBadge from './moduleFace/PassFailBadge';
import ModuleCardActions from './moduleFace/ModuleCardActions';
import { Link, useNavigate } from 'react-router-dom';

const DURATION_LABELS = {
    'isReady': 'Is Ready',
    '1 mins': '1 Min',
    '2 mins': '2 Mins',
    '3mins': '3 Mins',
    '5mins': '5 Mins',
    '10mins': '10 Mins',
    '15mins': '15 Mins',
    '30mins': '30 Mins',
    '2 days': '2 Days',
};

const getModuleUrl = (moduleName) => {
    return moduleName.trim().toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
}

function ModuleCard({
    name = 'Module Name',
    subTitle = '',
    logoAlt = 'Module Logo',
    description,
    module,
    user,
    permissionLevel,
    moduleCertification,
    getFitInvestorFirms,
    type,
    setTab,
    company,
    setIsWorkflowReport,
    seeMore = null,
    howItWorks = null,
    imageURL,
    showPercentages,
    setCompanyDashboardTab,
    menus,
    reportMenu,
    report = null,
    setTabName
}) {
    const defaultLogo = require('../../../images/icon.png');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isCreatingIRM, setIsCreatingIRM] = useState(false);
    const [isCreatingER, setIsCreatingER] = useState(false);
    const resolvedLogo = imageURL || module?.imageURL || defaultLogo;
    const moduleSlug = module?.slug?.includes('business-audit') ? `${module?.slug}-${company?.['testType']}` : module?.slug || type || getModuleUrl(name);
    const descriptionText = description ?? module?.moduleDescription ?? '';

    // ── Derived state from module fields ─────────────────────────────────────
    const isStarted = module?.isStarted || false;
    const completenessPercent = module?.completenessPercent || 0;
    const riskPercent = module?.riskPercent || 0;
    const sectionRestriction = module?.sectionAccessRestrictions?.filter(r => r?.companyObjective?.toLowerCase() === company?.testType?.toLowerCase() && r?.subscriptionTier?.toLowerCase() === company?.subscription?.toLowerCase())[0]?.isRestricted || false;
    // ResearchAid report cards are always accessible from the dashboard (same as the ResearchAid tab) — never apply the subscription restriction to them
    const isResearchAidCard = module?.isResearchAid === true;
    // KPI Report is free for every user and has no sections an admin could grant permission on, so it must never be
    // permission-gated — otherwise members are blocked with no way for the admin to unblock them.
    const isKpiReportCard = String(module?.['moduleKey'] || '').toLowerCase() === 'kpi' || /kpi report/i.test(String(module?.['moduleName'] || name || ''));
    // Reason: a card can expose a parent section number while permissions are granted per sub-section — InvestorMatch's
    // card carries sections: [{ sectionNo: 'DEPF008' }] but the permissions tree grants DEPF008.1 … DEPF008.4 — so a
    // grant on any child must satisfy the parent. Values are read as truthy, since legacy permissions store 1, not true.
    const hasSectionPermission = (perms, sectionNo) => {
        if (!perms || !sectionNo) return false;
        if (perms[sectionNo]) return true;
        const childPrefix = `${sectionNo}.`;
        return Object.keys(perms).some(key => key.startsWith(childPrefix) && perms[key]);
    };
    const isMemberPermissionRestricted = (() => {
        if (isKpiReportCard) return false;
        if (((user?.['role'] !== 'User' && user?.['role'] !== 'Member') || !user?.['createdBy']) || (user['createdBy']['role'].toLowerCase() !== 'user') || company?.['creatorId']?.['_id']?.toString() === user?.['_id']?.toString()) return false;
        const memberPerms = company?.['memberPermissions']?.[user['_id']];
        const sections = module?.sections || [];
        const sectionsWithNo = sections.filter(s => s['sectionNo']);
        if (module?.['moduleKey'] && hasSectionPermission(memberPerms, module['moduleKey'])) return false;
        if (sectionsWithNo.some(s => hasSectionPermission(memberPerms, s['sectionNo']))) return false;
        if (!module?.['moduleKey'] && !sectionsWithNo.length) return false;
        return true;
    })();
    const isRestricted = !isResearchAidCard && (sectionRestriction || module?.isRestricted === true || isMemberPermissionRestricted);
    const isPaid = module?.isPaid || false;
    const hasBoughtExpension = Array.isArray(company?.boughtExpensions) && company.boughtExpensions.some(id => id?.toString() === module?._id?.toString());
    const hasPurchased = hasBoughtExpension || !isPaid || module?.hasPurchased === true
    // console.log(!isPaid, module?.hasPurchased === true, hasBoughtExpension, "isPaid, hasPurchased -----function------> ModuleCard")

    const showRestrictedMessage = () => {
        showMessage('This module is restricted for your subscription. You need to upgrade.');
    };

    const isRestrictedBlocked = () => {
        if (!isRestricted) return false;
        showRestrictedMessage();
        return true;
    };

    const openPaidModulePurchaseModal = () => {
        if (isRestrictedBlocked()) return;
        dispatch(toggleModal({
            open: 'subscriptionPaymentConfirm',
            purchasePlan: 'tempModulePurchase',
            purchaseDefaultStep: 'tempModulePurchase',
            modulePurchase: {
                tempModuleId: module?._id,
                moduleName: module?.moduleName || name,
                price: module?.price,
                subscriptionTier: company?.subscription,
                companyObjective: company?.testType,
                stripeProductPriceId: module?.stripeProductPriceId,
            },
        }));
    };

    const handleIRMCreate = async () => {
        setIsCreatingIRM(true);
        // Step 1: fetch the IRM partner to get its _id (key CLIMB26 is the IRM partner)
        const partnerRes = await getRequest('/checklist/partner/KPMG');
        if (partnerRes?.code !== 200 || !partnerRes?.body?.['_id']) {
            setIsCreatingIRM(false);
            showMessage('Investment Readiness configuration not found', 'error');
            return;
        }
        // Step 2: trigger the standard checklist creation (step 0 = all-in-one run).
        // genType:'automatic' → GPT generates section names when partner has no fixedSections/dynamicSections.
        // totalQuestions:100 → 10 sections × 10 questions each (totalQuestions / 10 per section in addDDSections).
        const isHybrid = partnerRes['body']['checklist'] === 'Hybrid' && partnerRes['body']['fixedSections'] && partnerRes['body']['dynamicSections'];
        const payload = {
            partnerId: partnerRes['body']['_id'],
            category: 'partner',
            companies: { [company['_id']]: company['cName'] },
            memberPermissions: { [user['_id']]: { ...user, company: { _id: company['_id'] } } },
            totalQuestions: 100,
            ...(!isHybrid && { genType: 'automatic' }),
        };
        const res = await postRequest('/checklist/create/0', payload);
        setIsCreatingIRM(false);
        if (res?.code !== 200) {
            showMessage(res?.message || 'Failed to create Investment Readiness module', 'error');
            return;
        }
        // reset=true forces setTab to re-fetch fresh section/question data after creation
        navigate(`/company/${company['_id']}#${moduleSlug}`);
        setTab(moduleSlug, true);
    };

    const handleExitCreate = async () => {
        setIsCreatingER(true);
        // Fetch the exit-readiness partner by testType ('exit' resolves via testType fallback in getPartner).
        const partnerRes = await getRequest('/checklist/partner/ESFX');
        if (partnerRes?.code !== 200 || !partnerRes?.body?.['_id']) {
            setIsCreatingER(false);
            showMessage('Exit Readiness configuration not found', 'error');
            return;
        }
        const isHybrid = partnerRes['body']['checklist'] === 'Hybrid' && partnerRes['body']['fixedSections'] && partnerRes['body']['dynamicSections'];
        const payload = {
            partnerId: partnerRes['body']['_id'],
            category: 'partner',
            companies: { [company['_id']]: company['cName'] },
            memberPermissions: { [user['_id']]: { ...user, company: { _id: company['_id'] } } },
            totalQuestions: 100,
            ...(!isHybrid && { genType: 'automatic' }),
        };
        const res = await postRequest('/checklist/create/0', payload);
        setIsCreatingER(false);
        if (res?.code !== 200) {
            showMessage(res?.message || 'Failed to create Exit Readiness module', 'error');
            return;
        }
        navigate(`/company/${company['_id']}#${moduleSlug}`);
        setTab(moduleSlug, true);
    };

    const setTabWithRestrictionCheck = async (nextTab) => {
        if (isRestrictedBlocked()) return;
        if (user?.['role'] === 'Member' && company?.['creatorId']?.['_id']?.toString() !== user?.['_id']?.toString()) {
            const userPerms = company?.['memberPermissions']?.[user['_id']];
            const sectionNos = (module?.['sections'] || []).map(s => s?.['sectionNo']).filter(Boolean);
            const hasAnyPermission = sectionNos.some(sNo => hasSectionPermission(userPerms, sNo));
            if (sectionNos.length && userPerms && !hasAnyPermission && !isKpiReportCard && !['Exit Readiness', 'Investment Readiness'].includes(module?.['moduleName'])) {
                const adminName = company?.['creatorId']?.['firstName'] ? `${company['creatorId']['firstName']} ${company['creatorId']['lastName'] || ''}`.trim() : 'user';
                showMessage(`You don't have permission for this module. Please contact your admin ${adminName} to gain access`);
                return;
            }
        }
        if (module?.isDirectorsModule) {
            dispatch(toggleModal({ open: 'showTempDirectors', tempDirectorsModuleSlug: module.slug || moduleSlug }));
            return;
        }
        if (module?.isNavigationModule) {
            setCompanyDashboardTab(nextTab.includes('#') ? nextTab.replace('#', '') : `${nextTab}`)
        } else {
            return setTab(nextTab);
        }
    };

    // Open an already-generated ResearchAid report in the report viewer tab (instead of the generate page)
    const openResearchAidReportView = () => {
        const reportId = report?.['_id'] || module?.reportId;
        const reportType = report?.['reportType'] || module?.externalUrl;
        if (!reportId || !reportType) return;
        setTab(`${reportId}?${reportType}-researchaid`);
        if (setCompanyDashboardTab) setCompanyDashboardTab('Dashboard');
    };

    const durationLabel = module?.duration && module.duration !== 'NIL'
        ? (DURATION_LABELS[module.duration] || null)
        : null;

    // ResearchAid: use embedded module data as fallback when report prop is null
    const effectiveScore = report?.['score'] ?? module?.reportScore;
    const effectiveCreatedBy = report?.['createdBy'] ?? module?.reportCreatedBy;
    const effectiveCreatedAt = report?.['createdAt'] ?? module?.reportCreatedAt;
    const effectiveReportId = report?.['_id'] ?? module?.reportId;
    const effectiveReportType = report?.['reportType'] ?? module?.externalUrl;
    const isReportGenerated = !!(report?.['htmlReport']?.length || module?.isReportGenerated || (effectiveScore && effectiveCreatedBy));
    const effectiveReport = report || ((module?.isReportGenerated || (effectiveScore && effectiveReportId)) ? { _id: effectiveReportId, reportType: effectiveReportType } : null);

    const showStats = isStarted || hasBoughtExpension;
    const showCompleteness = (showStats || module?.completeness === 'Yes') && module?.completeness === 'Yes';
    const showRisk = (showStats || module?.risks === 'Yes') && module?.risks === 'Yes';
    const showCertification = showStats && module?.certification === 'Yes';
    const showPassFail = showStats && module?.passFail === 'Yes';

    const subscriptionLabel = (() => {
        const regex = /(audit|repository|anylist|Workflow)/i;

        if (regex.test(module?.moduleName)) {
            return company?.subscription?.toUpperCase();
        }
        return null
    })();

    const hasPassed = completenessPercent >= 70;

    const navigateSection = async (type, sectionNo) => {
        if (isPaid && !hasPurchased) {
            openPaidModulePurchaseModal();
            return;
        }
        if (user?.['role'] == 'Guest') {
            dispatch(toggleModal({ open: 'noRegistrationReport' }));
            return;
        }
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD' || permissionLevel == 'DASHBOARD & REPORT')) {
            showMessage(`You have ${permissionLevel} access only!`);
            return;
        }
        if (['software-development', 'esg', 'warranty-disclosure', 'directors-questionnaire'].includes(type)) {
            const message = `You don't have permission for this module. Please contact your admin ${company['creatorId']['firstName'] ? `${company['creatorId']['firstName']} ${company['creatorId']['lastName'] ? company['creatorId']['lastName'] : ""}` : 'user'} to gain access`;
            if (user['role'] == 'Member' && company['creatorId']['_id'] != user['_id']) {
                if (!(company?.['memberPermissions']?.[user['_id']]?.[module?.['sections']?.[0]?.['sectionNo']])) {
                    showMessage(message);
                    return;
                }
            }
            if ((user['role'] == "Advisor" || user['role'] == 'Investor') && company['creatorId']['_id'] != user['_id']) {
                if (!(company?.[user['role'] == "Investor" ? 'investors' : 'advisors']?.[user['_id']]?.['modules']?.[module?.['sections']?.[0]?.['sectionNo']])) {
                    showMessage(message);
                    return;
                }
            }
        }
        await setTabWithRestrictionCheck(type);
        navigate(`/company/${company['_id']}#${type}`);
        setTimeout(() => {
            let element = document.getElementById(sectionNo);
            if (element) element.scrollIntoView({ behavior: 'instant' }, true);
        }, 10000);
    };

    const setReportTab = async (type) => {
        if (isRestrictedBlocked()) return;
        if (isPaid && !hasPurchased) {
            openPaidModulePurchaseModal();
            return;
        }
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD')) {
            showMessage('You have Dashboard access only!');
            return;
        }
        if (type == 'investor-lead') {
            getFitInvestorFirms();
            return;
        }
        setTabWithRestrictionCheck(type + '-report');
    };

    return (
        <div
            className='bg1 new-shadow df fdc jcsb pr'
            style={{ height: '200px', border: '1px solid #B5BFDE', borderRadius: '20px', padding: '1.25rem' }}
        >
            {(module?.menusConfig?.length > 0 || module?.reportMenu?.length > 0) && (
                <ModuleCardMenusNew
                    module={module}
                    onNavigateSection={navigateSection}
                    setReportTab={setReportTab}
                    setIsWorkflowReport={setIsWorkflowReport}
                    setCompanyDashboardTab={setCompanyDashboardTab}
                    setTabWithRestrictionCheck={setTabWithRestrictionCheck}
                    type={type}
                    setTab={setTab}
                    company={company}
                    seeMore={seeMore}
                    howItWorks={howItWorks}
                    menus={menus}
                    reportMenu={reportMenu}
                />
            )}

            <div className='df fdc jcsb' style={{ flex: 1 }}>

                {/* ── TOP: icon + name + subscription badge ───────────────── */}
                <ModuleCardHeader
                    module={module}
                    resolvedLogo={resolvedLogo}
                    logoAlt={logoAlt}
                    subTitle={subTitle}
                    moduleTagColor={module?.moduleTagColor}
                    name={name}
                    setTab={setTabWithRestrictionCheck}
                    moduleSlug={moduleSlug}
                    company={company}
                    subscriptionLabel={subscriptionLabel}
                    isPaid={isPaid}
                    hasPurchased={hasPurchased}
                    isRestricted={isRestricted}
                    onRestrictedClick={showRestrictedMessage}
                    onRequirePurchase={openPaidModulePurchaseModal}
                    externalUrl={module?.externalUrl}
                    isResearchAid={module?.isResearchAid || false}
                    isReportGenerated={module?.isReportGenerated || false}
                    reportId={module?.reportId || null}
                    onOpenResearchAidReport={openResearchAidReportView}
                    isNavigationModule={module?.isNavigationModule || false}
                    isDirectorsModule={module?.isDirectorsModule || false}
                    tempDirectorsModuleSlug={module?.slug || moduleSlug}
                    isMemberPermissionRestricted={isMemberPermissionRestricted}
                    isInvestmentReadiness={module?.isInvestmentReadiness || false}
                    isExitReadiness={module?.isExitReadiness || false}
                    needToCreate={module?.needToCreate || false}
                    onNeedToCreate={module?.isExitReadiness ? handleExitCreate : handleIRMCreate}
                    isCreatingIRM={module?.isExitReadiness ? isCreatingER : isCreatingIRM}
                    isResearchAidReportGenerated={(module?.isResearchAid && isReportGenerated)}
                    report={effectiveReport}
                    setTabName={setTabName}
                />
                {/* ── BOTTOM: research aid report content OR standard stats ── */}
                {module?.isResearchAid ? (
                    <div className={`w100 df jcsb ${effectiveReportType === 'TCAR3' ? 'mt1rem' : 'mt2rem'}`}>
                        {!effectiveScore && !isReportGenerated && !(effectiveReportType === 'swotAnalysis' && report?.['disableSwotReport']) &&
                            <Link to={`/researchAid/${company['_id']}?${effectiveReportType}`} state={{ company }} className="c1 fs0-75rem fw600 td250 br25px px2rem py0-5rem cp bg4 bg3-hover">Generate</Link>
                        }
                        {(effectiveScore && effectiveCreatedBy) ? (
                            <div className="cp w100 df jcsb" onClick={() => { setTab(`${effectiveReportId}?${effectiveReportType}-researchaid`); if (setCompanyDashboardTab) setCompanyDashboardTab('Dashboard'); }}>
                                {company['subscription'] === 'Free'
                                    ? <div className='df fdc'>
                                        <p className='w100 fs0-75rem fw300'>Executive Summary only.</p>
                                        <p className='w100 fs0-75rem fw300'>To <span className='c7 fw500'>UNLOCK</span> upgrade to PRO/LITE</p>
                                    </div>
                                    : <div className='df aic'>
                                        <span data-tooltip={`${effectiveCreatedBy?.firstName ?? ''} ${effectiveCreatedBy?.lastName ?? ''}`} className="shadow w40px h40px bg16 bw3px bss bo19 df jcc aic br100 c1">
                                            {effectiveCreatedBy?.profile
                                                ? <img src={effectiveCreatedBy.profile} className="new-shadow w30px h30px df jcc aic br100 bg16 c1" />
                                                : <span className='bg16 c15 br50px'>{effectiveCreatedBy?.firstName ? (effectiveCreatedBy.firstName[0] + (effectiveCreatedBy.lastName?.[0] || '')) : '?'}</span>
                                            }
                                        </span>
                                        <p className='fs0-75rem c15 ml0-5rem'>{moment(effectiveCreatedAt).format('ll')}</p>
                                    </div>
                                }
                                <div className='df fdc'>
                                    <p className={`fs1-5rem tac ${effectiveScore >= 4 ? 'c34' : effectiveScore >= 2 ? 'c39' : 'c4'}`}>
                                        {effectiveScore}<span className='fs0-75rem'>/5</span>
                                    </p>
                                    <p className='fs0-75rem fw400 c15'>Report <br /> Score</p>
                                </div>
                            </div>
                        ) : (effectiveReportType === 'swotAnalysis' && report?.['disableSwotReport']) && (
                            <span className="fs0-75rem fw300 c14 tti">Please complete the other 4 reports prior to generating this report</span>
                        )}
                        { }
                    </div>
                ) : (
                    <div className='w100 df aife jcsb' style={{ gap: '0.5rem' }}>

                        {/* LEFT — description (not started/purchased) | percentages (started/purchased) */}
                        {((!isPaid && showStats && (showCompleteness || showRisk)) || (isPaid && hasPurchased && showCompleteness && showRisk)) ? (
                            <ModuleCardStats
                                showCompleteness={showCompleteness}
                                completenessPercent={completenessPercent}
                                showRisk={showRisk}
                                riskPercent={riskPercent}
                                isInvestorMatch={module?.isInvestorMatch || false}
                                strongMatchCount={module?.strongMatchCount || 0}
                                mediumMatchCount={module?.mediumMatchCount || 0}
                            />
                        ) : (
                            <ModuleCardDescription descriptionText={descriptionText} />
                        )}

                        {/* RIGHT — duration + buy (not started/purchased) | certification / pass-fail (started/purchased) */}
                        {((!isPaid && showStats) || (isPaid && hasPurchased)) ? (
                            <div className='df aic jcfe' style={{ flexShrink: 0 }}>
                                {showCertification && (
                                    <CertificationBadge moduleCertification={moduleCertification} />
                                )}
                                {showPassFail && !showCertification && (
                                    <PassFailBadge
                                        resolvedLogo={resolvedLogo}
                                        logoAlt={logoAlt}
                                        hasPassed={hasPassed}
                                    />
                                )}
                            </div>
                        ) : (
                            <ModuleCardActions durationLabel={durationLabel} isPaid={isPaid && !hasPurchased} onBuyClick={openPaidModulePurchaseModal} />
                        )}

                    </div>
                )}
            </div>
        </div >
    );
}

export default ModuleCard;
